// Whether ONE of the owner's own material rows fits how THIS section is produced -- the single definition the
// Malzeme dropdown (MaterialSelect), the add/manage button label (ManageOwnMaterialsButton) and the "add a
// material first" gate (ManufacturingLineLists#ownMaterialMissing) all read, so the three can never disagree on
// what "a material that fits" means. It mirrors the server-side material score exactly:
//   - carton (Frank::MaterialScores::CartonMaterialUserPrice) splits on the bobbin flag: a row is a bobbin-roll
//     row or a folded-sheet row, so it fits ONLY the matching bobbin state. An ABSENT flag is a NON-bobbin row.
//   - hard cardboard (Frank::MaterialScores::HardCardboardMaterialUserPrice) is built around the dieline's board
//     thickness -- a row WITHIN THICKNESS_MATCH_TOLERANCE_MM of that thickness fits (a stocked size a whole step
//     off is the wrong board), mirroring the scorer's nearest-within-tolerance auto-pick; until the thickness is
//     known, none do.
//   - corrugated cardboard fits ONLY the box's flute -- the drawing's Material#corrugated_cardboard_material_type,
//     threaded as Frank::Coordinator#corrugated_cardboard_flute and compared to the row's own `type` token (e.g.
//     'f_flute') -- so an F-flute box is offered no A/B/C/E-flute rows. An unknown flute -- or a flute-less
//     (ton-priced) row, whose `type` is blank -- leaves every row fitting rather than none (a corrugated section
//     always HAS a flute, so an unknown one is a resolution gap, not a not-yet-known thickness). The scorer itself
//     is flute-agnostic; this is a picker-only narrowing.
//   - every other material key has no such split, so every row fits.

// The band within which a stocked board counts as the dieline's -- mirrors the Ruby constant
// Frank::MaterialScores::HardCardboardMaterialUserPrice::THICKNESS_MATCH_TOLERANCE_MM (keep the two in sync). It
// absorbs an inch-converted thickness landing just off a stocked one (1.2954mm vs 1.3mm) while rejecting a whole
// size jump (1.2mm vs 3.0mm), so these surfaces show exactly the board the scorer prices.
export const THICKNESS_MATCH_TOLERANCE_MM = 0.05;

export const materialFitsSection = (material, { materialKey, bobbinEnabled, boardThicknessMm, corrugatedCardboardFlute }) => {
  if (materialKey === 'carton_material') {
    return Boolean(material.bobbin_enabled) === Boolean(bobbinEnabled);
  }
  if (materialKey === 'hard_cardboard_material') {
    const hasBoardThickness = boardThicknessMm !== undefined && boardThicknessMm !== null;
    return hasBoardThickness
      && Math.abs(Number(material.thickness_in_mm) - Number(boardThicknessMm)) <= THICKNESS_MATCH_TOLERANCE_MM;
  }
  if (materialKey === 'corrugated_cardboard_material') {
    // Falls open (every row fits) when the box flute is unknown, OR the row has no flute of its own -- a
    // weight-priced (ton) corrugated material is flute-agnostic (settings.yml hides its `type`) and fits any box,
    // so it must not vanish from the picker. Otherwise only the rows of the box's flute fit.
    if (!corrugatedCardboardFlute || !material.type) { return true; }
    return material.type === corrugatedCardboardFlute;
  }
  return true;
};

// The rows of `materials` that fit the section, by the same test above.
export const compatibleMaterials = (materials, criteria) => (
  materials.filter((material) => materialFitsSection(material, criteria))
);

// The filter a material form applies to its frequently-used ("Sık kullanılanlar") system list: hard cardboard is
// narrowed to the boards within tolerance of the drawing thickness, corrugated cardboard to the box's flute, every
// other material key left whole. Both material-form trees (frank MaterialForm and the layout QuoteBuilderForm)
// build the picker's filter from this, so the rule AND its "is the criterion known?" gate live in one place. A
// criterion that is absent (thickness null/undefined/0, or no flute) leaves the list unfiltered.
export const systemItemsFilter = (materialKey, { boardThicknessMm, corrugatedCardboardFlute } = {}) => (items) => {
  if (materialKey === 'hard_cardboard_material' && boardThicknessMm > 0) {
    return compatibleMaterials(items, { materialKey, boardThicknessMm });
  }
  if (materialKey === 'corrugated_cardboard_material' && corrugatedCardboardFlute) {
    return compatibleMaterials(items, { materialKey, corrugatedCardboardFlute });
  }
  return items;
};
